import { useCallback, useEffect, useRef, useState } from 'react';

import {
  BUTTON_HUMAN_LOADING,
  ERROR_ANIMATION_DURATION,
} from './Button.constants';

interface UseHumanLoadingButtonProps {
  isLoading: boolean;
  minimumDuration?: number;
}

export const useHumanLoadingButton = ({
  isLoading,
  minimumDuration = BUTTON_HUMAN_LOADING,
}: UseHumanLoadingButtonProps): boolean => {
  const isMounted = useRef(true);
  const [delayedLoading, setDelayedLoading] = useState(isLoading);

  useEffect(() => {
    if (isLoading) {
      setDelayedLoading(true);
      window.setTimeout(() => {
        if (isMounted.current) {
          setDelayedLoading(false);
        }
      }, minimumDuration);
    }
  }, [isLoading, minimumDuration]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return isLoading || delayedLoading;
};

export const useErrorAnimation = (isError: boolean) => {
  const [isErrorAnimationReady, setIsErrorAnimationReady] = useState(false);
  const [hasPendingShakeRequest, setHasPendingShakeRequest] = useState(false);
  const [shakeVersion, setShakeVersion] = useState(0);

  const resetShake = useCallback(() => {
    if (isError) {
      setShakeVersion((currentVersion) => currentVersion + 1);

      return;
    }

    setHasPendingShakeRequest(true);
  }, [isError]);

  useEffect(() => {
    if (!isError || !hasPendingShakeRequest) {
      return undefined;
    }

    setHasPendingShakeRequest(false);
    setShakeVersion((currentVersion) => currentVersion + 1);
  }, [hasPendingShakeRequest, isError]);

  useEffect(() => {
    if (shakeVersion === 0) {
      return undefined;
    }

    setIsErrorAnimationReady(true);

    const timeoutId = window.setTimeout(() => {
      setIsErrorAnimationReady(false);
    }, ERROR_ANIMATION_DURATION);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [shakeVersion]);

  return { isErrorAnimationReady, resetShake };
};
